/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { recaptchaMessage } from '../configs';
import {
  MutationObserverInit,
  ReCaptchaV3Model,
} from '../types/recaptcha.types';
const { failedExecutionRecaptcha } = recaptchaMessage;

export const getRecaptchaToken = async (
  websiteKey: string
): Promise<string> => {
  if (!(window as any).grecaptcha) {
    return Promise.reject(failedExecutionRecaptcha);
  }

  try {
    const token = await window.grecaptcha.execute(websiteKey, {
      action: 'click',
    });

    return token;
  } catch (error) {
    return Promise.reject(`${failedExecutionRecaptcha} : ${error}`);
  }
};

export const waitForReCaptcha = () => {
  return new Promise((resolve) => {
    const observer = new MutationObserver((_, obs) => {
      if (window.grecaptcha) {
        obs.disconnect();
        resolve(true);
      }
    });

    const observerOptions: MutationObserverInit = {
      childList: true,
      subtree: true,
      attributes: true,
    };

    observer.observe(document.body, observerOptions);
  });
};

export const verifyReCaptchaLoad = async (
  badgeId: string,
  config: ReCaptchaV3Model,
  logger: boolean
): Promise<void> => {
  if (!window.grecaptcha) {
    await waitForReCaptcha();
  }

  return grecaptcha.ready(() => {
    const badgeContainers = document.querySelectorAll(`#${badgeId}`);

    if (!badgeContainers.length) return;

    // Handle the case when multiple instances of the drop-in container rendered on the same page

    badgeContainers.forEach(
      (element) => (element.id = `${element.id}_${Math.random().toString(36)}`) // NOSONAR
    );

    badgeContainers.forEach((element) => {
      if (element.innerHTML === '') {
        try {
          grecaptcha.render(element.id, {
            sitekey: config.websiteKey as string,
            badge: config.badgePosition,
            size: 'invisible',
            theme: config.theme ?? 'light',
          });
        } catch (error) {
          logger && console.error(error);
        }
      }
    });
  });
};
